import React from 'react'
import { Spin, Icon, Pagination } from 'antd'
import OutfitCard from './OutfitCard'
import './OutfitGrid.scss'

const getNumericCount = value => {
  if (value === undefined || value === null || value === '') return null

  const count = Number(value)
  return Number.isFinite(count) ? count : null
}

const getFirstNumericCount = values => {
  for (let i = 0; i < values.length; i += 1) {
    const count = getNumericCount(values[i])
    if (count !== null) return count
  }

  return null
}

/**
 * Grid layout container for outfit cards
 *
 * @param {Object} props
 * @param {Array} props.outfits - List of outfits
 * @param {Function} props.onItemClick - Item click handler
 * @param {Boolean} props.isLoading - Show loading spinner
 * @param {String} props.emptyMessage - Empty state message
 * @param {Boolean} props.showDelete - Show delete button on cards
 * @param {Function} props.onDelete - Delete handler for cards
 * @param {Object} props.pagination - Pagination data object
 * @param {Function} props.onPageChange - Page change handler
 * @param {Boolean} props.clampDescriptions - Limit card descriptions to two lines
 */
const OutfitGrid = ({
  outfits = [],
  onItemClick,
  isLoading = false,
  emptyMessage = 'No outfits found',
  showDelete = false,
  onDelete,
  selectedId = null,
  pagination = null,
  onPageChange,
  clampDescriptions = false
}) => {
  const { systemOutfits, customOutfits } = React.useMemo(() => {
    return {
      systemOutfits: outfits.filter(o => o.type === 'system' || o.is_system),
      customOutfits: outfits.filter(o => o.type !== 'system' && !o.is_system)
    }
  }, [outfits])

  const paginationTotal = getNumericCount(pagination && pagination.total)
  const hasSystemSection = systemOutfits.length > 0
  const hasCustomSection = customOutfits.length > 0

  const systemPaginationCount = getFirstNumericCount([
    pagination && pagination.system_total,
    pagination && pagination.systemTotal
  ])
  const systemOutfitCount =
    systemPaginationCount !== null
      ? systemPaginationCount
      : systemOutfits.length

  const customPaginationCount = getFirstNumericCount([
    pagination && pagination.custom_total,
    pagination && pagination.customTotal,
    pagination && pagination.user_total,
    pagination && pagination.userTotal,
    pagination && pagination.my_collections_total,
    pagination && pagination.myCollectionsTotal,
    !hasSystemSection && hasCustomSection ? paginationTotal : null
  ])
  const customOutfitCount =
    customPaginationCount !== null
      ? customPaginationCount
      : customOutfits.length

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="outfit-grid-loading">
          <Spin size="large" tip="Loading outfits..." />
        </div>
      )
    }

    if (!outfits || outfits.length === 0) {
      return (
        <div className="outfit-grid-empty">
          <Icon type="skin" className="empty-icon" />
          <p>{emptyMessage}</p>
        </div>
      )
    }

    return (
      <div className="outfit-groups-container">
        {systemOutfits.length > 0 && (
          <div className="outfit-section">
            <div className="section-title">
              <Icon type="thunderbolt" style={{ color: '#faad14' }} />
              System Styles
              <span className="count-tag">{systemOutfitCount}</span>
            </div>
            <div className="outfit-grid">
              {systemOutfits.map(outfit => (
                <OutfitCard
                  key={outfit.id || outfit.tag}
                  outfit={outfit}
                  onClick={onItemClick}
                  showDelete={showDelete}
                  onDelete={onDelete}
                  isSelected={selectedId === (outfit.id || outfit.tag)}
                  clampDescription={clampDescriptions}
                />
              ))}
            </div>
          </div>
        )}

        {customOutfits.length > 0 && (
          <div className="outfit-section">
            <div className="section-title">
              <Icon type="user" style={{ color: '#5057d5' }} />
              My Collections
              <span className="count-tag">{customOutfitCount}</span>
            </div>
            <div className="outfit-grid">
              {customOutfits.map(outfit => (
                <OutfitCard
                  key={outfit.id || outfit.tag}
                  outfit={outfit}
                  onClick={onItemClick}
                  showDelete={showDelete}
                  onDelete={onDelete}
                  isSelected={selectedId === (outfit.id || outfit.tag)}
                  clampDescription={clampDescriptions}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="outfit-grid-wrapper">
      {renderContent()}

      {pagination && pagination.total > (pagination.page_size || 20) && (
        <div className="pagination-container">
          <Pagination
            current={pagination.page || 1}
            total={pagination.total || 0}
            pageSize={pagination.page_size || 20}
            onChange={onPageChange}
            disabled={isLoading}
            showSizeChanger={false}
          />
        </div>
      )}
    </div>
  )
}

export default OutfitGrid
